import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { adminClientFacilities } from '../BaseURL';
import ClaimsImport from './ClaimsImport';
import ImportHistory from './ImportHistory';
import { IconPlus, IconClaims } from './icons';

// ============================================================================
//  ClaimsImportPanel.js — the Claims XML Import screen
// ============================================================================
//
//  THE SCREEN IS THE IMPORT RECORDS. Importing is an occasional act; reading what
//  has already been imported is the daily one, and the records are the part with
//  a table, an expandable batch, a skip report and thousands of claims in it.
//
//  Two earlier shapes and why neither held:
//
//    STACKED     the importer sat on top — a facility gate plus a drop zone —
//                so the records began below the fold. Checking what an import did
//                meant scrolling down, then back up to load the next file.
//    SIDE BY SIDE the importer took a fixed column, which starved it: the parsed
//                preview it shows after a file is chosen is a summary strip, a
//                warnings banner, four counters, three attendance filters and a
//                claims table. At 440px that wraps into a ribbon, while the
//                records table — the wider of the two by far — lost a third of
//                its width to a panel that is empty most of the time.
//
//  So: records full width, and the importer in a DIALOG behind a button. A dialog
//  is the right container for it because importing is modal in the real sense —
//  you pick a facility, choose a file, read the preview and commit, and nothing
//  else on the screen matters until you are done.
//
//  ClaimsImport now reports completion (`onImported`), so the dialog closes itself
//  and the records reload with the new batch in place. That replaces an 8-second
//  polling timer which refreshed the records whether or not anything had happened
//  and showed a finished import up to 8 seconds late.

export default function ClaimsImportPanel() {
  const [facilities, setFacilities] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [importerOpen, setImporterOpen] = useState(false);

  //  The batch that just landed, so the records list can say so instead of the
  //  new row appearing silently among the others.
  const [justImported, setJustImported] = useState(null);

  const loadFacilities = useCallback(async () => {
    try {
      const { data } = await axios.get(adminClientFacilities, { params: { active: 1 } });
      // Only onboarded facilities can receive a batch — claims.facility_id is a
      // foreign key into client_facilities.
      setFacilities(data?.facilities || data?.rows || []);
    } catch (err) {
      console.error('[claims import] facilities', err?.response?.data?.error || err.message);
      setFacilities([]);
    }
  }, []);

  useEffect(() => { loadFacilities(); }, [loadFacilities]);

  //  ── What happens when a batch finishes ──────────────────────────────────
  //  The dialog stays open for a moment on purpose. The importer's own summary is
  //  the only place that reports the parse warnings and the per-reason skips for
  //  the run just completed, and yanking it away the instant the last claim lands
  //  would take that with it. A second and a half is long enough to register the
  //  success and short enough not to feel stuck.
  const handleImported = useCallback((job) => {
    setJustImported(job || null);
    setRefreshKey((k) => k + 1);
    const t = setTimeout(() => setImporterOpen(false), 1500);
    return () => clearTimeout(t);
  }, []);

  //  Closing by hand also refreshes. A commit that finished while the dialog was
  //  open has already bumped the key; this covers the case where it was closed
  //  mid-run and the job completed unobserved.
  const closeImporter = useCallback(() => {
    setImporterOpen(false);
    setRefreshKey((k) => k + 1);
  }, []);

  return (
    <>
      {/*  The records own the page. The New import button lives in their header —
           see ImportHistory's `action` prop — so there is one thing on screen and
           one way to add to it. */}
      <ImportHistory
        refreshKey={refreshKey}
        highlightJobId={justImported?.id || null}
        action={(
          <button
            type="button"
            className="ih-new-btn"
            onClick={() => { setJustImported(null); setImporterOpen(true); }}
          >
            <IconPlus size={15} />
            New import
          </button>
        )}
      />

      {/*  ── The importer, in a dialog ──────────────────────────────────────
           NOT the shared ICT <Modal>: its widest size is 780px and the importer's
           preview needs more than that — a claims table with seven columns inside
           780px is the ribbon this change exists to stop. It also puts
           `overflow: hidden` on the body, which is right, but the importer needs
           its own scroll region rather than the card growing past the viewport.

           MOUNTED ONLY WHILE OPEN, which is what makes each import start clean:
           the previous file, its preview and its finished job all go with the
           unmount, so opening it again cannot show the last batch's numbers. The
           trade is that closing mid-commit loses the progress view — the job
           itself keeps running on the server and lands in the records, and the
           records are where it is then read. */}
      {importerOpen && (
        <div
          className="ci-modal-scrim"
          role="dialog"
          aria-modal="true"
          aria-label="Import a claims batch"
          onClick={closeImporter}
        >
          <div className="ci-modal-card" onClick={(e) => e.stopPropagation()}>
            {/* ── Header ──────────────────────────────────────────────────────
                A navy band rather than a tinted strip, matching every other
                dialog in the product — the plain bordered header read as an
                unstyled browser panel, which is the "raw" of it.

                It carries three things the previous one did not: what the file
                must be, what will happen to a claim with problems, and how many
                facilities are available to pick from. That last one matters
                because an empty dropdown was once the whole failure mode here. */}
            <header className="ci-modal-head">
              <span className="ci-modal-icon" aria-hidden="true"><IconClaims size={19} /></span>
              <div className="ci-modal-copy">
                <div className="ci-modal-eyebrow">NHIA e-Claims</div>
                <div className="ci-modal-title">Import a batch</div>
                <div className="ci-modal-sub">
                  Pick the facility, then drop or browse for the XML. The month and
                  year come from each claim&apos;s own <code>dateOfService</code>, so
                  they are never typed.
                </div>
                <div className="ci-modal-facts">
                  <span className="ci-modal-fact">
                    {facilities.length} facilit{facilities.length === 1 ? 'y' : 'ies'} available
                  </span>
                  <span className="ci-modal-fact">
                    A warning never drops a claim
                  </span>
                  <span className="ci-modal-fact">
                    Claims already on file are refreshed, not duplicated
                  </span>
                </div>
              </div>
              <button
                type="button"
                className="ci-modal-close"
                onClick={closeImporter}
                aria-label="Close"
              >
                ✕
              </button>
            </header>

            <div className="ci-modal-body">
              <ClaimsImport facilities={facilities} onImported={handleImported} />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
